import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Check, Crown, Zap, Users, Database, Shield } from 'lucide-react';

/**
 * "Plan y Facturación", adentro de su plegable en la Configuración del panel.
 *
 * La presentación se rehízo para que se parezca a /admin/plans: superficie
 * blanca con borde tenue, el naranja de la marca por token y los botones de la
 * app. Antes cada control traía su color escrito a mano —botón azul para
 * cambiar, rojo para cancelar, verde para reactivar, un degradado violeta a
 * rosa para enterprise y una barra de progreso celeste—, o sea seis colores que
 * no salían de ningún lado y que no aparecen en ninguna otra pantalla.
 *
 * El color de estado (verde/rojo/ámbar) sí se queda: no es marca, es semáforo.
 *
 * OJO con las animaciones acá adentro: `.dashboard-container *` en
 * Dashboard.css sólo deja transicionar color, sombra y opacidad, y apaga las
 * animaciones. La barra de progreso tenía `transition-all duration-500` y un
 * `animate-pulse` que en el panel nunca corrieron.
 */

/**
 * El precio con separador de miles y símbolo. `price` llega como número suelto
 * y antes se pintaba `${price}` con `/mo` al lado: un plan de $14.999 se leía
 * "$14999/mo", con formato y unidad de otro idioma.
 */
const formatearPrecio = (precio, moneda) =>
  new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: moneda || 'ARS',
    maximumFractionDigits: 0,
  }).format(precio || 0);

const CurrentPlan = ({
  planName = "Pro Plan",
  planType = "pro",
  billingCycle = "monthly",
  price = 29,
  currency = "ARS",
  // `usageData` (almacenamiento en GB, usuarios, proyectos) y su
  // `calculateUsagePercentage` venían de la plantilla de la que salió este
  // componente y no los leía nadie: Aturno no mide nada de eso.
  features = null,
  nextBillingDate = null,
  freeTrialEndDate = null,
  planStatus = "active", // active, suspended, cancelled, inactive
  subscriptionId = null,
  onUpgrade = () => console.log("Upgrade clicked"),
  onManage = () => console.log("Manage clicked")
}) => {
  const [currentTime, setCurrentTime] = useState(new Date());

  // Actualizar el tiempo cada minuto para mostrar el trial actualizado
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Cada 60 segundos

    return () => clearInterval(interval);
  }, []);

  // Convert features from object format to array format if needed
  const normalizeFeatures = (features) => {
    // If features is null, undefined, or not provided, return default features
    if (!features) {
      return [
        { name: "Gestión de personal", included: false },
        { name: "Gestión de servicios", included: false },
        { name: "Gestión de recursos", included: false },
        { name: "Gestión de turnos", included: false },
        { name: "Analíticas avanzadas", included: false },
        { name: "Soporte prioritario", included: false }
      ];
    }
    
    // If features is already an array, return it
    if (Array.isArray(features)) {
      return features;
    }
    
    // If features is an object (from API), convert to array format
    if (typeof features === 'object') {
      const featureMap = {
        maxStaff: { name: "Gestión de Personal", included: Boolean(features.maxStaff && features.maxStaff > 0) },
        maxServices: { name: "Gestión de Servicios", included: Boolean(features.maxServices && features.maxServices > 0) },
        maxResources: { name: "Gestión de Recursos", included: Boolean(features.maxResources && features.maxResources > 0) },
        maxBookingsPerMonth: { name: "Reservas Mensuales", included: Boolean(features.maxBookingsPerMonth && features.maxBookingsPerMonth !== 0) },
        customBranding: { name: "Personalización de Marca", included: Boolean(features.customBranding) },
        analytics: { name: "Analytics Avanzados", included: Boolean(features.analytics) },
        integrations: { name: "Integraciones", included: Boolean(features.integrations) },
        multiLocation: { name: "Múltiples Ubicaciones", included: Boolean(features.multiLocation) }
      };
      
      return Object.values(featureMap);
    }
    
    // Final fallback
    return [
      { name: "Gestión de personal", included: false },
      { name: "Gestión de servicios", included: false },
      { name: "Gestión de recursos", included: false },
      { name: "Gestión de turnos", included: false },
      { name: "Analíticas avanzadas", included: false },
      { name: "Soporte prioritario", included: false }
    ];
  };

  const normalizedFeatures = normalizeFeatures(features);
  
  // Ensure normalizedFeatures is always an array
  const safeFeatures = Array.isArray(normalizedFeatures) ? normalizedFeatures : [];

  const calculateTrialTimeRemaining = () => {
    const trialInfo = getTrialDayInfo();
    
    if (trialInfo.daysRemaining <= 0) {
      return 'Trial expirado';
    }
    
    // Si queda más de un día, mostrar solo días
    if (trialInfo.daysRemaining > 1) {
      return `${trialInfo.daysRemaining} días`;
    }
    
    // Si queda un día o menos, mostrar horas y minutos precisos
    if (!freeTrialEndDate) return 'No disponible';
    
    const trialEndDate = new Date(freeTrialEndDate);
    const now = currentTime;
    const diffMs = trialEndDate - now;
    
    if (diffMs <= 0) return 'Trial expirado';
    
    const totalHours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (totalHours > 0) {
      return `${totalHours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  const getTrialDayInfo = () => {
    if (!freeTrialEndDate || freeTrialEndDate === 'null' || freeTrialEndDate === null) {
      return { currentDay: 0, totalDays: 14, percentage: 0, daysRemaining: 0 };
    }
    
    const trialEndDate = new Date(freeTrialEndDate);
    const now = currentTime;
    
    // Si el trial ya expiró
    if (now >= trialEndDate) {
      return { currentDay: 14, totalDays: 14, percentage: 100, daysRemaining: 0 };
    }
    
    // Calculamos cuántos días quedan
    const msRemaining = trialEndDate - now;
    const daysRemaining = Math.ceil(msRemaining / (1000 * 60 * 60 * 24));
    
    // El trial dura 14 días
    const totalTrialDays = 14;
    
    // Calculamos la fecha de inicio del trial (14 días antes del fin)
    const trialStartDate = new Date(trialEndDate);
    trialStartDate.setDate(trialStartDate.getDate() - totalTrialDays);
    
    // Si estamos antes del inicio calculado, asumimos día 1
    if (now < trialStartDate) {
      return { currentDay: 1, totalDays: totalTrialDays, percentage: 0, daysRemaining: Math.min(daysRemaining, totalTrialDays) };
    }
    
    // Calculamos cuánto tiempo ha pasado desde el inicio
    const msElapsed = now - trialStartDate;
    const daysElapsed = msElapsed / (1000 * 60 * 60 * 24);
    
    // Calculamos el día actual (1-14)
    const currentDay = Math.floor(daysElapsed) + 1;
    
    // Calculamos el porcentaje basado en el tiempo transcurrido
    const percentage = (daysElapsed / totalTrialDays) * 100;
    
    
    return { 
      currentDay: Math.max(1, Math.min(currentDay, totalTrialDays)), 
      totalDays: totalTrialDays, 
      percentage: Math.min(100, Math.max(0, percentage)),
      daysRemaining: Math.min(daysRemaining, totalTrialDays)
    };
  };

  const calculateTrialPercentage = () => {
    return getTrialDayInfo().percentage;
  };

  const isInTrial = () => {
    // If user has an active subscription, they're not in trial
    if (subscriptionId && planStatus === 'active') {
      return false;
    }
    
    // If no trial end date, not in trial
    if (!freeTrialEndDate || freeTrialEndDate === 'null' || freeTrialEndDate === null) {
      return false;
    }
    
    const trialEndDate = new Date(freeTrialEndDate);
    const now = currentTime;
    return trialEndDate > now;
  };

  // Semáforo del estado de la suscripción. Es lo único que conserva color
  // propio: acá el color informa, no decora.
  const getStatusColor = () => {
    switch (planStatus) {
      case 'active':
        return 'bg-emerald-50 text-emerald-700 ring-emerald-200';
      case 'suspended':
        return 'bg-red-50 text-red-700 ring-red-200';
      case 'cancelled':
        return 'bg-amber-50 text-amber-700 ring-amber-200';
      default:
        return 'bg-neutral-100 text-neutral-600 ring-neutral-200';
    }
  };

  const getStatusText = () => {
    switch (planStatus) {
      case 'active':
        return 'Activo';
      case 'suspended':
        return 'Suspendido';
      case 'cancelled':
        if (nextBillingDate) {
          const endDate = new Date(nextBillingDate).toLocaleDateString('es-AR');
          return `Cancelado - Activo hasta ${endDate}`;
        }
        return 'Cancelado';
      default:
        return 'Inactivo';
    }
  };

  const getPlanIcon = () => {
    switch (planType) {
      case 'trial':
        return <Users className="h-5 w-5" />;
      case 'pro':
        return <Zap className="h-5 w-5" />;
      case 'enterprise':
        return <Crown className="h-5 w-5" />;
      default:
        return <Users className="h-5 w-5" />;
    }
  };

  // El chip del ícono. `bg-primary/10 text-primary` es el mismo par que usa el
  // tilde de TarjetaDePlan, así que el panel y la página de planes se ven de la
  // misma familia sin repetir un solo color escrito a mano.
  const getPlanColor = () => {
    switch (planType) {
      case 'trial':
        return 'bg-amber-50 text-amber-600';
      case 'enterprise':
        return 'bg-neutral-900 text-white';
      case 'pro':
        return 'bg-primary/10 text-primary';
      default:
        return 'bg-neutral-100 text-neutral-600';
    }
  };

  const infoTrial = getTrialDayInfo();

  return (
    <div className="w-full overflow-hidden rounded-2xl border border-neutral-200 bg-white">
      {/* Cabecera: quién sos, cuánto pagás y qué podés hacer al respecto. */}
      <div className="flex flex-col gap-6 p-6 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-4">
          <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${getPlanColor()}`}>
            {getPlanIcon()}
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-xl font-bold text-neutral-900">{planName}</h3>
              <span
                className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${getStatusColor()}`}
              >
                {getStatusText()}
              </span>
            </div>
            <p className="mt-1 text-sm text-neutral-500">
              Tu plan actual · Facturación {billingCycle === 'monthly' ? 'mensual' : 'anual'}
            </p>
            <p className="mt-4 flex items-baseline gap-1">
              <span className="text-3xl font-bold tracking-tight text-neutral-900">
                {price > 0 ? formatearPrecio(price, currency) : 'Gratis'}
              </span>
              {price > 0 && (
                <span className="text-sm text-neutral-500">
                  /{billingCycle === 'monthly' ? 'mes' : 'año'}
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Los botones de la app. `destructive` y `outline` salen de los tokens
            de button.jsx: cancelar ya no es un rojo suelto escrito acá. */}
        <div className="flex shrink-0 flex-wrap gap-2">
          {planStatus !== 'cancelled' ? (
            <Button variant="outline" size="sm" onClick={onManage}>
              Cancelar suscripción
            </Button>
          ) : (
            <Button size="sm" onClick={onUpgrade}>
              Reactivar plan
            </Button>
          )}
          {planType !== 'enterprise' && planStatus !== 'cancelled' && (
            <Button size="sm" onClick={onUpgrade}>
              Cambiar de plan
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-6 border-t border-neutral-200 p-6">
        {/* Información del Trial o Suscripción */}
        {isInTrial() ? (
          <div>
            <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-neutral-900">
              <Database className="h-4 w-4 text-neutral-400" aria-hidden="true" />
              Prueba gratuita
            </h4>

            <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4">
              <div className="mb-2 flex justify-between text-sm">
                <span className="text-neutral-700">Progreso de los 14 días</span>
                <span className="text-neutral-500">
                  Día {infoTrial.currentDay} de {infoTrial.totalDays}
                </span>
              </div>
              {/* Sin `transition` ni `animate-*`: el panel los apaga (ver el
                  comentario de arriba). La barra se pinta ya en su posición. */}
              <div className="h-2.5 w-full overflow-hidden rounded-full bg-neutral-200">
                <div
                  className="h-full rounded-full bg-primary"
                  style={{ width: `${calculateTrialPercentage()}%` }}
                />
              </div>
              <div className="mt-2 flex justify-between text-xs text-neutral-500">
                <span>{Math.round(calculateTrialPercentage())}% usado</span>
                <span className="font-semibold text-neutral-700">
                  Quedan {calculateTrialTimeRemaining()}
                </span>
              </div>
            </div>

            <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm text-amber-800">
                Tu prueba termina el{' '}
                <strong>
                  {freeTrialEndDate && freeTrialEndDate !== 'null' && freeTrialEndDate !== null
                    ? new Date(freeTrialEndDate).toLocaleDateString('es-AR')
                    : 'fecha no disponible'}
                </strong>
                . Después de esa fecha vas a necesitar elegir un plan para seguir.
              </p>
            </div>
          </div>
        ) : (
          <div>
            <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-neutral-900">
              <Shield className="h-4 w-4 text-neutral-400" aria-hidden="true" />
              Suscripción
            </h4>

            <dl className="divide-y divide-neutral-200 rounded-xl border border-neutral-200">
              <div className="flex items-center justify-between gap-4 px-4 py-3 text-sm">
                <dt className="text-neutral-600">Próxima facturación</dt>
                <dd className="font-medium text-neutral-900">
                  {nextBillingDate && nextBillingDate !== 'null' && nextBillingDate !== null
                    ? new Date(nextBillingDate).toLocaleDateString('es-AR')
                    : 'No disponible'}
                </dd>
              </div>
              {subscriptionId && (
                <div className="flex items-center justify-between gap-4 px-4 py-3 text-sm">
                  <dt className="text-neutral-600">ID de suscripción</dt>
                  <dd className="truncate font-mono text-xs text-neutral-500">{subscriptionId}</dd>
                </div>
              )}
            </dl>

            {planStatus === 'suspended' && (
              <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-4">
                <p className="text-sm text-red-800">
                  Tu suscripción está suspendida por un pago que no se pudo cobrar. Actualizá el
                  medio de pago para reactivar el servicio.
                </p>
              </div>
            )}

            {planStatus === 'cancelled' && (
              <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
                <p className="text-sm font-semibold text-amber-800">
                  Suscripción cancelada · acceso temporal
                </p>
                {nextBillingDate ? (
                  <>
                    <p className="mt-1 text-sm text-amber-800">
                      Mantenés el acceso completo hasta el{' '}
                      <strong>{new Date(nextBillingDate).toLocaleDateString('es-AR')}</strong>. No
                      se te va a cobrar nada más después de esa fecha.
                    </p>
                    <p className="mt-2 text-xs font-semibold text-amber-700">
                      {(() => {
                        const diffDays = Math.ceil(
                          (new Date(nextBillingDate) - new Date()) / (1000 * 60 * 60 * 24)
                        );
                        return diffDays > 0 ? `Quedan ${diffDays} días` : 'Vence hoy';
                      })()}
                    </p>
                  </>
                ) : (
                  <p className="mt-1 text-sm text-amber-800">
                    Podés reactivarla eligiendo un plan nuevo.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Lo que incluye */}
        <div>
          <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-neutral-900">
            <Check className="h-4 w-4 text-neutral-400" aria-hidden="true" />
            Lo que incluye tu plan
          </h4>
          <ul className="grid list-none grid-cols-1 gap-x-6 gap-y-2.5 md:grid-cols-2">
            {safeFeatures.map((feature) => (
              <li key={feature.name} className="flex items-start gap-2.5">
                {/* El mismo tilde que TarjetaDePlan cuando está incluido; un
                    círculo apagado cuando no, en vez del tachado gris. */}
                <span
                  className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                    feature.included ? 'bg-primary/10 text-primary' : 'bg-neutral-100 text-neutral-300'
                  }`}
                  aria-hidden="true"
                >
                  <Check className="h-3 w-3" strokeWidth={3} />
                </span>
                <span
                  className={`text-sm leading-relaxed ${
                    feature.included ? 'text-neutral-700' : 'text-neutral-400'
                  }`}
                >
                  {feature.name}
                  {feature.limit && <span className="ml-1 text-neutral-400">({feature.limit})</span>}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CurrentPlan; 